import { supabase } from '@/integrations/supabase/client'
import { processEmailProvider } from './lead-email-provider-service'

// Real-time subscription for processing new email events
export class LeadEmailProviderRealtimeService {
  private static instance: LeadEmailProviderRealtimeService
  private channel: any = null
  private isSubscribed = false

  private constructor() {}

  static getInstance(): LeadEmailProviderRealtimeService {
    if (!LeadEmailProviderRealtimeService.instance) {
      LeadEmailProviderRealtimeService.instance = new LeadEmailProviderRealtimeService()
    }
    return LeadEmailProviderRealtimeService.instance
  }

  /**
   * Initialize real-time subscription for new email events
   */
  public initialize(): void {
    if (this.isSubscribed) {
      console.log('🔔 Lead email provider real-time service already initialized')
      return
    }

    console.log('🔔 Initializing real-time subscription for lead_email_provider processing')

    this.channel = supabase
      .channel('lead_email_provider_realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'obsync_dynamic_reports',
          filter: 'event_type=in.(EMAIL_SENT,EMAIL_REPLY,EMAIL_BOUNCE)'
        },
        async (payload) => {
          console.log('🔔 New email event received for lead_email_provider processing:', payload.new)
          
          const event = payload.new
          const toEmail = event.to_email
          const eventId = event.id
          const eventType = event.event_type

          // Only process if lead_email_provider is null
          if (!event.lead_email_provider && toEmail) {
            try {
              console.log(`🔄 Processing lead_email_provider for ${eventType}: ${toEmail}`)
              
              // Import the processEmailProvider function dynamically to avoid circular imports
              const { processEmailProvider } = await import('./lead-email-provider-service')
              const provider = await processEmailProvider(toEmail)
              
              if (provider) {
                // Update the record with the detected provider
                const { error: updateError } = await supabase
                  .from('obsync_dynamic_reports')
                  .update({ lead_email_provider: provider })
                  .eq('id', eventId)
                
                if (updateError) {
                  console.error(`❌ Error updating lead_email_provider for ${eventId}:`, updateError)
                } else {
                  console.log(`✅ Updated lead_email_provider for ${eventId}: ${toEmail} → ${provider}`)
                }
              } else {
                console.warn(`⚠️ Could not detect provider for ${toEmail}`)
              }
            } catch (error) {
              console.error(`❌ Error processing lead_email_provider for ${eventId}:`, error)
            }
          } else {
            console.log(`⏭️ Skipping ${eventId}: lead_email_provider already set or no to_email`)
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          this.isSubscribed = true
          console.log('✅ Lead email provider real-time subscription active')
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Lead email provider real-time subscription error')
          this.isSubscribed = false
        }
      })
  }

  /**
   * Stop the real-time subscription
   */
  public stop(): void {
    if (this.channel && this.isSubscribed) {
      console.log('🛑 Stopping lead email provider real-time subscription')
      supabase.removeChannel(this.channel)
      this.channel = null
      this.isSubscribed = false
    }
  }

  /**
   * Get subscription status
   */
  public getStatus(): { isSubscribed: boolean; channel: any } {
    return {
      isSubscribed: this.isSubscribed,
      channel: this.channel
    }
  }
}

// Export singleton instance
export const leadEmailProviderRealtime = LeadEmailProviderRealtimeService.getInstance()
